import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { Box, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE } from '../householdModelPortfolio.constants';
import type { HouseholdModelPortfolioIpsTargetAllocationRow } from '../householdModelPortfolio.types';
import { HouseholdModelPortfolioDetailField } from './householdModelPortfolioDetailField';

type HouseholdModelPortfolioIpsTargetAllocationsProps = {
  label: string;
  rows: HouseholdModelPortfolioIpsTargetAllocationRow[];
  testId: string;
};

export const HouseholdModelPortfolioIpsTargetAllocations = ({
  label,
  rows,
  testId,
}: HouseholdModelPortfolioIpsTargetAllocationsProps) => {
  const { t } = useTranslation(['components', 'modelPortfolios']);

  return (
    <Box data-testid={testId}>
      <Typography
        variant='bodySmall'
        component='div'
        color='textSecondary'
        sx={{ mb: 1 }}
      >
        {label}
      </Typography>
      {rows.length === 0 ? (
        <Typography
          variant='bodyMedium'
          sx={{ fontWeight: 500 }}
          data-testid={`${testId}-empty`}
        >
          {t('components:householdModelPortfolio.ips.noTargetAllocations')}
        </Typography>
      ) : (
        <Table data-testid={`${testId}-table`}>
          <TableHead>
            <TableRow>
              <TableHeadCell>{t('modelPortfolios:assetClass')}</TableHeadCell>
              <TableHeadCell>{t('modelPortfolios:min')}</TableHeadCell>
              <TableHeadCell>{t('modelPortfolios:max')}</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={`${row.assetClassName}-${row.min}-${row.max}`}
                data-testid={`${testId}-row-${row.assetClassName}`}
              >
                <TableCell>{row.assetClassName}</TableCell>
                <TableCell>{row.min}</TableCell>
                <TableCell>{row.max}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

type HouseholdModelPortfolioIpsRichTextFieldProps = {
  label: string;
  value: string;
  testId: string;
};

const HouseholdModelPortfolioIpsRichTextField = ({
  label,
  value,
  testId,
}: HouseholdModelPortfolioIpsRichTextFieldProps) => (
  <Box data-testid={testId}>
    <Typography
      variant='bodySmall'
      component='div'
      color='textSecondary'
      sx={{ mb: 0.5 }}
    >
      {label}
    </Typography>
    {value === HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE ? (
      <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    ) : (
      <Typography
        variant='bodyMedium'
        component='div'
        sx={{
          '& p': { margin: 0 },
          '& p + p': { marginTop: 1 },
          fontWeight: 500,
        }}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    )}
  </Box>
);

type HouseholdModelPortfolioIpsFieldProps = {
  field: {
    id: string;
    label: string;
    kind: 'text' | 'richText' | 'targetAllocations';
    value?: string;
    targetAllocations?: HouseholdModelPortfolioIpsTargetAllocationRow[];
  };
};

export const HouseholdModelPortfolioIpsField = ({
  field,
}: HouseholdModelPortfolioIpsFieldProps) => {
  const testId = `household-model-portfolio-ips-field-${field.id}`;

  switch (field.kind) {
    case 'richText':
      return (
        <HouseholdModelPortfolioIpsRichTextField
          label={field.label}
          value={field.value ?? HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE}
          testId={testId}
        />
      );
    case 'targetAllocations':
      return (
        <HouseholdModelPortfolioIpsTargetAllocations
          label={field.label}
          rows={field.targetAllocations ?? []}
          testId={testId}
        />
      );
    default:
      return (
        <HouseholdModelPortfolioDetailField
          testId={testId}
          label={field.label}
          value={field.value ?? HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE}
        />
      );
  }
};
